
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface SignupFormProps {
  onSubmit: (email: string, password: string, username: string) => Promise<void>;
  isLoading: boolean;
}

const SignupForm = ({ onSubmit, isLoading }: SignupFormProps) => {
  const [signupData, setSignupData] = useState({
    email: "",
    password: "",
    username: "",
    confirmPassword: "",
  });

  const [errors, setErrors] = useState<{
    email?: string;
    password?: string;
    confirmPassword?: string;
    username?: string;
    form?: string;
  }>({});

  const validatePassword = (password: string) => {
    if (password.length < 8) {
      return "Password must be at least 8 characters long";
    }
    
    if (!/[A-Z]/.test(password)) {
      return "Password must contain at least one uppercase letter";
    }
    
    if (!/[a-z]/.test(password)) {
      return "Password must contain at least one lowercase letter";
    }
    
    if (!/[0-9]/.test(password)) {
      return "Password must contain at least one number";
    }
    
    return "";
  };

  const validateForm = () => {
    const newErrors: {
      email?: string;
      password?: string;
      confirmPassword?: string;
      username?: string;
    } = {};
    
    // Email validation
    if (!signupData.email) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(signupData.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    
    // Username validation
    if (!signupData.username) {
      newErrors.username = "Username is required";
    } else if (signupData.username.length < 3) {
      newErrors.username = "Username must be at least 3 characters";
    }
    
    // Password validation
    const passwordError = validatePassword(signupData.password);
    if (passwordError) {
      newErrors.password = passwordError;
    }
    
    // Confirm password validation
    if (signupData.password !== signupData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      await onSubmit(signupData.email, signupData.password, signupData.username);
    } catch (error) {
      setErrors({ 
        form: "An error occurred during signup. Please try again." 
      });
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="space-y-4">
        {errors.form && (
          <Alert variant="destructive">
            <AlertDescription>{errors.form}</AlertDescription>
          </Alert>
        )}
        
        <div className="space-y-2">
          <Label htmlFor="signup-email" className="flex justify-between">
            <span>Email</span>
            {errors.email && <span className="text-xs text-destructive font-normal">{errors.email}</span>}
          </Label>
          <Input 
            id="signup-email" 
            type="email" 
            placeholder="your@email.com" 
            required 
            value={signupData.email}
            className={errors.email ? "border-destructive" : ""}
            onChange={(e) => {
              setSignupData({...signupData, email: e.target.value});
              if (errors.email) setErrors({...errors, email: undefined});
            }}
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="username" className="flex justify-between">
            <span>Username</span>
            {errors.username && <span className="text-xs text-destructive font-normal">{errors.username}</span>}
          </Label>
          <Input 
            id="username" 
            placeholder="Your username" 
            required 
            value={signupData.username}
            className={errors.username ? "border-destructive" : ""}
            onChange={(e) => {
              setSignupData({...signupData, username: e.target.value});
              if (errors.username) setErrors({...errors, username: undefined});
            }}
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="signup-password" className="flex justify-between">
            <span>Password</span>
            {errors.password && <span className="text-xs text-destructive font-normal">{errors.password}</span>}
          </Label>
          <Input 
            id="signup-password" 
            type="password" 
            required 
            value={signupData.password}
            className={errors.password ? "border-destructive" : ""}
            onChange={(e) => {
              setSignupData({...signupData, password: e.target.value});
              if (errors.password) setErrors({...errors, password: undefined});
            }}
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="confirm-password" className="flex justify-between">
            <span>Confirm Password</span>
            {errors.confirmPassword && <span className="text-xs text-destructive font-normal">{errors.confirmPassword}</span>}
          </Label>
          <Input 
            id="confirm-password" 
            type="password" 
            required 
            value={signupData.confirmPassword}
            className={errors.confirmPassword ? "border-destructive" : ""}
            onChange={(e) => {
              setSignupData({...signupData, confirmPassword: e.target.value});
              if (errors.confirmPassword) setErrors({...errors, confirmPassword: undefined});
            }}
          />
        </div>
        
        <Button 
          type="submit" 
          className="w-full" 
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Creating account...
            </>
          ) : (
            "Create Account"
          )}
        </Button>
        
        <div className="text-xs text-muted-foreground space-y-1 mt-2">
          <p>Password must:</p>
          <ul className="list-disc list-inside">
            <li>Be at least 8 characters long</li>
            <li>Contain at least one uppercase letter</li>
            <li>Contain at least one lowercase letter</li>
            <li>Contain at least one number</li>
          </ul>
        </div>
      </div>
    </form>
  );
};

export default SignupForm;
